import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Transition } from "@headlessui/react";
import { ListFilter } from "lucide-react";
import { Button, Input, Modal, Table, Toggle } from "src/components";
import FilterAccordion from "src/container/filter-accordion.component";
import { RootState } from "src/context/store";
import { useOutsideClick } from "src/hooks/useOutsideClick";
import { usePagination } from "src/hooks/usePagination"; //M10-11654
import { toast } from "src/hooks/useToast";
import { getAssigneeList, updateInvoiceAssigneeFlags } from "src/services/invoice-api";

type AllocationField = "is_round_robin_applicable" | "is_rejection_reviewer" | "is_einvoice_reviewer";
type YesNoOption = { key: "Y" | "N"; value: "Yes" | "No" };

const YES_NO_OPTIONS: YesNoOption[] = [
  { key: "Y", value: "Yes" },
  { key: "N", value: "No" },
];

const Divider = () => <div className="w-full h-[0.0625rem] bg-[#0000001a]" />;

const FIELD_LABELS: Record<AllocationField, string> = {
  is_round_robin_applicable: "File Allocation",
  is_rejection_reviewer: "Rejection Reviewer",
  is_einvoice_reviewer: "E-Invoice Reviewer", // M10-16200
};

// Maps an /invoice-assignees row to the fixed shape the table columns below expect.
const normalizeAllocationRow = (row: any) => ({
  invoice_assignment_id: row?.invoice_assignment_id,
  user_name: row?.user_name,
  is_round_robin_applicable: !!row?.is_round_robin_applicable,
  is_rejection_reviewer: !!row?.is_rejection_reviewer,
  is_einvoice_reviewer: !!row?.is_einvoice_reviewer, // M10-16200
});

const getAllocationColumns = (
  onToggle: (row: any, field: AllocationField, value: boolean) => void,
  updatingKey: string | null
) => [
  {
    id: "sno",
    header: "Sno",
    cell: (info: any) => info.row.index + 1,
  },
  {
    header: "User Name",
    accessorKey: "user_name",
  },
  {
    header: "File Allocation",
    accessorKey: "is_round_robin_applicable",
    cell: (info: any) => {
      const row = info.row.original;
      return (
        <Toggle
          checked={info.getValue()}
          disabled={updatingKey === `${row.invoice_assignment_id}:is_round_robin_applicable`}
          onChange={(value) => onToggle(row, "is_round_robin_applicable", value)}
        />
      );
    },
  },
  {
    header: "Rejection Reviewer",
    accessorKey: "is_rejection_reviewer",
    cell: (info: any) => {
      const row = info.row.original;
      return (
        <Toggle
          checked={info.getValue()}
          disabled={updatingKey === `${row.invoice_assignment_id}:is_rejection_reviewer`}
          onChange={(value) => onToggle(row, "is_rejection_reviewer", value)}
        />
      );
    },
  },
  // M10-16200: pool for round-robin e-invoice review
  {
    header: "E-Invoice Reviewer",
    accessorKey: "is_einvoice_reviewer",
    cell: (info: any) => {
      const row = info.row.original;
      return (
        <Toggle
          checked={info.getValue()}
          disabled={updatingKey === `${row.invoice_assignment_id}:is_einvoice_reviewer`}
          onChange={(value) => onToggle(row, "is_einvoice_reviewer", value)}
        />
      );
    },
  },
];

const Allocation = () => {
  const { InvoiceFilters } = useSelector((state: RootState) => state.invoice);
  const company_code = InvoiceFilters?.company?.[0]?.code;

  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [fileAllocationSelected, setFileAllocationSelected] = useState<YesNoOption[]>([]);
  const [rejectionReviewerSelected, setRejectionReviewerSelected] = useState<YesNoOption[]>([]);
  const [einvoiceReviewerSelected, setEinvoiceReviewerSelected] = useState<YesNoOption[]>([]); // M10-16200
  // Bumped on Reset to force the accordions to remount and drop their internal selection state.
  const [filterResetKey, setFilterResetKey] = useState(0);
  const filterPanelRef = useOutsideClick(() => setIsFilterPanelOpen(false));
  // `${invoice_assignment_id}:${field}` of the toggle currently being saved — disables just that toggle.
  const [updatingKey, setUpdatingKey] = useState<string | null>(null);
  // Toggle click asks for confirmation before the flag is actually flipped.
  const [pendingToggle, setPendingToggle] = useState<{ row: any; field: AllocationField; value: boolean } | null>(null);
  const { pagination, onPaginationChange, pageIndex, pageSize, resetPagination } = usePagination();//M10-11654

  // `silent` skips the page-level spinner — used for the post-toggle refetch so the whole
  // table doesn't flash away just to refresh the one row that changed.
  const fetchAllocationList = useCallback(
    async (silent = false) => {
      if (!silent) setIsLoading(true);
      try {
        const res: any = await getAssigneeList(company_code);
        const rows = res?.data?.data ?? [];
        setData((Array.isArray(rows) ? rows : []).map(normalizeAllocationRow));
      } catch (error) {
        console.error("Error fetching allocation list:", error);
        toast({ description: "Failed to fetch allocation list", variant: "destructive" });
        if (!silent) setData([]);
      } finally {
        if (!silent) setIsLoading(false);
      }
    },
    [company_code]
  );

  useEffect(() => {
    fetchAllocationList();
  }, [fetchAllocationList]);

  const handleToggle = useCallback(
    async (row: any, field: AllocationField, value: boolean) => {
      if (!row?.invoice_assignment_id) {
        toast({ description: "Missing assignment id for this row", variant: "destructive" });
        return;
      }

      const key = `${row.invoice_assignment_id}:${field}`;
      const previousValue = row[field];

      // Optimistic update — flip immediately, roll back if the API call fails.
      setData((prev) =>
        prev.map((item) => (item.invoice_assignment_id === row.invoice_assignment_id ? { ...item, [field]: value } : item))
      );
      setUpdatingKey(key);

      try {
        const res: any = await updateInvoiceAssigneeFlags(
          {
            data: { invoice_assignment_id: row.invoice_assignment_id, [field]: value },
            event_type: "INVOICE_ASSIGNMENT_FLAGS_UPDATE",
            primary_key: row.invoice_assignment_id,
          },
          company_code
        );

        // Response echoes the updated row synchronously; merge it straight in.
        const updatedRow = res?.data?.data?.data;
        setData((prev) =>
          prev.map((item) =>
            item.invoice_assignment_id === row.invoice_assignment_id
              ? { ...item, ...(updatedRow ? normalizeAllocationRow(updatedRow) : { [field]: value }) }
              : item
          )
        );
        toast({ description: "Updated successful" });
      } catch (error: any) {
        console.error("Error updating allocation flag:", error);
        const message = error?.response?.data?.message || "Failed to update, please try again";
        toast({ description: message, variant: "destructive" });
        setData((prev) =>
          prev.map((item) =>
            item.invoice_assignment_id === row.invoice_assignment_id ? { ...item, [field]: previousValue } : item
          )
        );
      } finally {
        setUpdatingKey(null);
      }
    },
    [company_code]
  );

  const requestToggle = useCallback((row: any, field: AllocationField, value: boolean) => {
    setPendingToggle({ row, field, value });
  }, []);

  const confirmPendingToggle = useCallback(() => {
    if (!pendingToggle) return;
    handleToggle(pendingToggle.row, pendingToggle.field, pendingToggle.value);
    setPendingToggle(null);
  }, [pendingToggle, handleToggle]);

  const cancelPendingToggle = useCallback(() => setPendingToggle(null), []);

  const columns = useMemo(() => getAllocationColumns(requestToggle, updatingKey), [requestToggle, updatingKey]);

  // getAssigneeList returns the full list in one shot — search + toggle filters are applied client-side.
  const filteredData = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    const matchesYesNo = (value: boolean, selected: YesNoOption[]) =>
      selected.length === 0 || selected.some((item) => (item.key === "Y") === value);

    return data.filter((row) => {
      if (query && !row.user_name?.toLowerCase().includes(query)) return false;
      if (!matchesYesNo(row.is_round_robin_applicable, fileAllocationSelected)) return false;
      if (!matchesYesNo(row.is_rejection_reviewer, rejectionReviewerSelected)) return false;
      if (!matchesYesNo(row.is_einvoice_reviewer, einvoiceReviewerSelected)) return false; // M10-16200
      return true;
    });
  }, [data, searchValue, fileAllocationSelected, rejectionReviewerSelected, einvoiceReviewerSelected]);
//M10-11654
  useEffect(() => {
    resetPagination();
  }, [searchValue, fileAllocationSelected, rejectionReviewerSelected, einvoiceReviewerSelected]);

  const pageStartIndex = (pageIndex - 1) * pageSize;
  const pagedData = useMemo(
    () => filteredData.slice(pageStartIndex, pageStartIndex + pageSize),
    [filteredData, pageStartIndex, pageSize]
  );
  const pageCount = Math.ceil(filteredData.length / pageSize);
  //M10-11654

  const filterCount =
    (fileAllocationSelected.length > 0 ? 1 : 0) +
    (rejectionReviewerSelected.length > 0 ? 1 : 0) +
    (einvoiceReviewerSelected.length > 0 ? 1 : 0);

  const handleResetFilters = () => {
    setFileAllocationSelected([]);
    setRejectionReviewerSelected([]);
    setEinvoiceReviewerSelected([]);
    setFilterResetKey((key) => key + 1);
  };

  return (
    <div className="w-full">
      <div className="flex items-center justify-end gap-3 py-6">
        <div className="flex items-center w-[16rem] rounded-md overflow-hidden border border-gray-300 bg-white">
          <Input
            placeholder="Search by User Name"
            debounceTime={500}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            containerClasses="w-full px-3 py-1 text-gray-700 border-none bg-white focus:outline-none focus:ring-0 focus:shadow-none"
          />
        </div>

        <button
          type="button"
          aria-label="Filter"
          onClick={() => setIsFilterPanelOpen(true)}
          className="relative flex items-center justify-center w-9 h-9 rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
        >
          <ListFilter className="w-4 h-4" />
          {filterCount > 0 && (
            <span className="absolute -top-1.5 -right-1.5 flex items-center justify-center w-4 h-4 text-[0.625rem] text-white bg-[#7F56D9] rounded-full">
              {filterCount}
            </span>
          )}
        </button>
      </div>

      <Transition
        as={Fragment}
        show={isFilterPanelOpen}
        enter="transition ease-in-out duration-200 transform"
        enterFrom="translate-x-full"
        enterTo="translate-x-0"
        leave="transition ease-in-out duration-200 transform"
        leaveFrom="translate-x-0"
        leaveTo="translate-x-full"
      >
        <aside
          ref={filterPanelRef as any}
          className="bg-white border-l-[1px] min-w-[20rem] max-w-[25rem] overflow-y-auto border-l-[#00000014] fixed px-8 text-[#49484C] py-5 right-0 top-0 h-screen z-[500]"
        >
          <div className="flex items-center justify-between gap-5 mt-3">
            <h3 className="text-xl font-semibold whitespace-nowrap">Fetch filters</h3>
            <Button className="border py-1 bg-[#7F56D9] text-white" onClick={handleResetFilters}>
              Reset
            </Button>
          </div>

          <div className="flex flex-col">
            <FilterAccordion
              key={`file-allocation-${filterResetKey}`}
              uniqueKey="key"
              displayKey="value"
              title="File Allocation"
              arrayToFilter={YES_NO_OPTIONS}
              defaultValue={fileAllocationSelected}
              onSelectingFilter={(value) => setFileAllocationSelected(value as YesNoOption[])}
            />
            <Divider />

            <FilterAccordion
              key={`rejection-reviewer-${filterResetKey}`}
              uniqueKey="key"
              displayKey="value"
              title="Rejection Reviewer"
              arrayToFilter={YES_NO_OPTIONS}
              defaultValue={rejectionReviewerSelected}
              onSelectingFilter={(value) => setRejectionReviewerSelected(value as YesNoOption[])}
            />
            <Divider />

            {/* M10-16200 */}
            <FilterAccordion
              key={`einvoice-reviewer-${filterResetKey}`}
              uniqueKey="key"
              displayKey="value"
              title="E-Invoice Reviewer"
              arrayToFilter={YES_NO_OPTIONS}
              defaultValue={einvoiceReviewerSelected}
              onSelectingFilter={(value) => setEinvoiceReviewerSelected(value as YesNoOption[])}
            />
          </div>
        </aside>
      </Transition>

      <Table
        columns={columns}
        data={pagedData}
        isLoading={isLoading}
        showSearch={false}
        headClass="bg-[#F8F8F8]"
        manualPagination
        pagination={pagination}
        onPaginationChange={onPaginationChange}
        pageCount={pageCount}
      />

      <Modal isOpen={!!pendingToggle} isHeader={false} onCloseModal={cancelPendingToggle}>
        {pendingToggle && (
          <div className="flex flex-col gap-y-2 mb-4 w-[340px] mx-auto px-6 bg-white p-4 rounded-xl">
            <p className="text-lg font-semibold text-gray-800">Confirm change</p>
            <p className="text-sm text-gray-600 mt-2">
              Are you sure you want to <span className="font-bold">{pendingToggle.value ? "enable" : "disable"}</span>{" "}
              {FIELD_LABELS[pendingToggle.field]} for{" "}
              <span className="font-medium">{pendingToggle.row?.user_name}</span>?
            </p>
            <div className="flex justify-end gap-2 mt-4">
              <Button className="bg-gray-300 text-gray-700 hover:bg-[#BFC5CC] px-4 py-2 rounded-lg" onClick={cancelPendingToggle}>
                Cancel
              </Button>
              <Button
                className="text-white bg-[#8051FF] hover:bg-[#6E3DFF] px-4 py-2 rounded-lg"
                onClick={confirmPendingToggle}
              >
                Proceed
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Allocation;
